/**
 * Utility functions
 */

import { BRAND_COLORS } from '@/constants';

// Style utilities
export const getBrandColor = (color: keyof typeof BRAND_COLORS) => BRAND_COLORS[color];

export const createInlineStyle = (styles: Record<string, string | number>) => styles;

// String utilities
export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const truncate = (str: string, length: number) => 
  str.length > length ? `${str.substring(0, length)}...` : str;

export const cleanTextCode = (text: string) => {
  return text
  .replace(/\u00A0|\u200B|\r/g, ' ') // remove non-breaking, zero-width, and carriage returns
  .replace(/[ \t]+/g, ' ')           // collapse tabs/spaces
  .replace(/\n\s*\n\s*\n+/g, '\n\n') // max one empty line between paragraphs
  .replace(/\s*\n\s*/g, '\n')        // trim spaces around line breaks
  .replace(/^\s+|\s+$/g, '')        // allow max one empty line
    .trim();                            // remove leading/trailing spaces
};

export const decodeContent = (base64Content: string, cleanText: boolean = false) => {
  try {
    const decoded = atob(base64Content); 
  
    // Convert Latin-1 bytes → UTF-8 string
    const utf8Decoder = new TextDecoder('utf-8');
    const bytes = Uint8Array.from(decoded, c => c.charCodeAt(0));
    if (cleanText) {
      return cleanTextCode(utf8Decoder.decode(bytes));
    }
    return utf8Decoder.decode(bytes);
  } catch (e) {
    return base64Content;
  }
};


// Validation utilities
export const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Array utilities
export const shuffle = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const groupBy = <T, K extends keyof T>(array: T[], key: K): Record<string, T[]> => {
  return array.reduce((groups, item) => {
    const group = String(item[key]);
    groups[group] = groups[group] || [];
    groups[group].push(item);
    return groups;
  }, {} as Record<string, T[]>);
};

// Date utilities
export const formatDate = (date: string | Date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const isToday = (date: string | Date) => {
  const today = new Date();
  const d = new Date(date);
  return d.toDateString() === today.toDateString();
};

// Number utilities
export const formatNumber = (num: number) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

// Object utilities
export const omit = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  const result = { ...obj };
  keys.forEach(key => delete result[key]);
  return result;
};

export const pick = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
};
